// @flow
import { PureComponent, createRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';
import { ConfigContext } from '@sdk/config/ConfigContext';
import { shouldApplyAutomatedBreaksForShift } from '@sdk/automatedBreaks/utils';

import ClockedInViewWithAutomatedBreaks from '@kiosk/common/components/UserPanel/ClockedInViewWithAutomatedBreaks/ClockedInViewWithAutomatedBreaks';
import { AutomatedBreaksProvider } from '@kiosk/common/components/UserPanel/ClockedInViewWithAutomatedBreaks/AutomatedBreaksProvider/AutomatedBreaksProvider';
import { ClockedInViewProvider } from '@kiosk/common/components/UserPanel/ClockedInViewWithAutomatedBreaks/ClockedInViewProvider/ClockedInViewProvider';

import type {
  Persona,
  Position,
  Screen,
  Shift,
  User,
  Note,
  AppState,
  AppScreenType,
  MultilineDescription,
  AdHocClockInActionCreator,
  SignOutActionCreator,
  ClockInActionCreator,
  StartBreakActionCreator,
  EndBreakActionCreator,
  ClockOutActionCreator,
  UpdateShiftActionCreator,
  StartAutomatedBreakActionCreator
} from '../../typings';
import FormContainer from '../Forms/FormContainer';
import BrandLoader from '../BrandLoader';
import getUpcomingShiftDescription from '../../../timeClock/utils/getUpcomingShiftDescription';
import whenIsNextAvailableClockIn from '../../../timeClock/utils/whenIsNextAvailableClockIn';
import { APP_STATE_CLOCKED_IN, SMALL_DEVICE_HEIGHT, SMALL_DEVICE_WIDTH } from '../../../constants';

import ClockedInFields from './ClockedInFields';
import UserPanelHeader from './UserPanelHeader';
import SingleShiftFields from './SingleShiftFields';
import MultiShiftFields from './MultiShiftFields';
import NoShiftFields from './NoShiftFields';

const Container = styled.div`
  display: flex;
  justify-content: flex-end;

  z-index: 6;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  min-height: ${props => props.deviceHeight}px;
  background-color: transparent;
  transform: translateX(${props => (props.shouldBringToFront ? 0 : props.deviceWidth)}px);
  transition: transform 0.7s ease-in-out;

  &.moveOut {
    transform: ${props => 'translateX(' + props.deviceWidth + 'px)'};
  }

  @media (max-height: ${SMALL_DEVICE_HEIGHT}px) {
    align-items: flex-start;
    overflow-y: auto;
    max-height: ${props => props.deviceHeight}px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 50%;
  background-color: ${props => props.theme.clockInBg};
  min-height: ${props => props.deviceHeight}px;

  @media (max-width: ${SMALL_DEVICE_WIDTH}px) {
    width: 100%;
  }
`;

const GrowingContainer = styled(FormContainer)`
  @media (max-height: ${SMALL_DEVICE_HEIGHT}px) {
    overflow-x: hidden;
    overflow-y: auto;
  }
`;

type Props = {
  user: User | null,
  personas?: Array<Persona>,
  shifts: Array<Shift>,
  shift: Shift | null,
  adHocShift: Shift | null,
  screen: Screen | null,
  notes: Array<Note> | null,
  appState: AppState,
  clockInEarlyMinutes: number,
  deviceWidth: number,
  deviceHeight: number,
  loadingUserShifts: boolean,
  appStatusVisible: boolean,
  usTimeFormat: boolean,
  shiftBreakType: boolean,
  hideGreeting: boolean,
  shouldBringToFront: boolean,
  isIntegrationKiosk: boolean,
  automatedBreaksIsEnabled: boolean,
  adHocClockIn: AdHocClockInActionCreator,
  signOut: SignOutActionCreator,
  clockIn: ClockInActionCreator,
  startBreak: StartBreakActionCreator,
  endBreak: EndBreakActionCreator,
  clockOut: ClockOutActionCreator,
  updateShift: UpdateShiftActionCreator,
  startAutomatedBreak: StartAutomatedBreakActionCreator
};

type State = {
  step: number,
  shouldDelayHeaderAnimation: boolean,
  shouldHideHeader: boolean,
  shouldMoveOut: boolean,
  positions: Array<Position>,
  locations: Array<Persona>
};

export default class UserPanel extends PureComponent<Props, State> {
  static contextType = ConfigContext;
  timeClockNoteRef = createRef<{ saveNote: () => void }>();

  componentDidMount() {
    if (this.props.personas) {
      this.setGroups(this.props.personas);
    }
  }

  state = {
    step: 0,
    shouldDelayHeaderAnimation: true,
    shouldHideHeader: false,
    shouldMoveOut: false,
    positions: [],
    locations: []
  };

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps({ screen, personas, appStatusVisible }: Props) {
    if (screen && !this.props.screen) {
      this.setState({ shouldMoveOut: true });
    } else if (!screen && this.props.screen) {
      this.setState({ shouldMoveOut: false });
    }

    if (personas && personas !== this.props.personas) {
      this.setGroups(personas);
    }

    if (appStatusVisible) {
      this.setState({ step: 0 });
    }
  }

  setGroups = (personas: Array<Persona>) => {
    this.setState({
      locations: personas.filter(persona => persona.type === 'location'),
      positions: ((personas: any).filter(persona => persona.type === 'position'): Array<Position>)
    });
  };

  clockInNextStep = (
    location?: Persona | null,
    position?: Persona | null,
    timeClockNote?: string | null
  ) => {
    if (location && position) {
      this.props.adHocClockIn(location.id, position.id, timeClockNote);
    } else if (this.state.step === 0 && location) {
      this.setState({
        step: 2,
        shouldDelayHeaderAnimation: true
      });
    } else {
      const targetStep = this.state.step + 1;
      const shouldDelayHeaderAnimation = targetStep !== 3;
      this.setState({
        step: targetStep,
        shouldDelayHeaderAnimation
      });
    }
  };

  clockInPrevStep = () => {
    if (this.state.step === 0) {
      this.props.signOut();
    } else {
      const targetStep = this.state.step - 1;
      const shouldDelayHeaderAnimation = targetStep !== 0;
      this.setState({
        step: targetStep,
        shouldDelayHeaderAnimation
      });
    }
  };

  signOut = () => {
    this.setState({ step: 0 });
    return this.props.signOut();
  };

  getCustomHeaderLabel = (
    type: AppScreenType,
    adHocShift: Shift | null,
    usTimeFormat: boolean,
    clockInEarlyMinutes: number
  ): MultilineDescription | null => {
    if (type !== 'noShift') {
      return null;
    }

    const { step, locations, positions } = this.state;

    if (!locations.length && !positions.length) {
      return {
        firstLine:
          'In order to clock in you must be added to at least one supported location and one position.'
      };
    }

    if (!locations.length) {
      return {
        firstLine: 'In order to clock in you must be added to at least one supported location.'
      };
    }

    if (!positions.length) {
      return {
        firstLine: 'In order to clock in you must be added to at least one position.'
      };
    }

    if (step === 0 || step === 3 || (positions.length === 1 && locations.length === 1)) {
      if (adHocShift) {
        return {
          firstLine: getUpcomingShiftDescription(adHocShift, usTimeFormat),
          secondLine: whenIsNextAvailableClockIn(adHocShift, clockInEarlyMinutes),
          thirdLine: 'Clock in for an unscheduled shift'
        };
      }

      return {
        firstLine: 'Clock in for an unscheduled shift'
      };
    }

    return null;
  };

  getClassNames(hideGreeting: boolean, shouldMoveOut: boolean): string {
    let className = '';

    if (hideGreeting) {
      className += 'useStateBg';
    }

    if (shouldMoveOut) {
      className += 'moveOut';
    }

    return className;
  }

  isGroupPickerStep() {
    return includes([1, 2], this.state.step);
  }

  saveNote = () => this.timeClockNoteRef.current?.saveNote();

  render() {
    const {
      user,
      loadingUserShifts,
      shifts,
      shift,
      adHocShift,
      personas,
      notes,
      clockIn,
      startBreak,
      startAutomatedBreak,
      endBreak,
      clockOut,
      usTimeFormat,
      clockInEarlyMinutes,
      shiftBreakType,
      appState,
      appStatusVisible,
      hideGreeting,
      deviceWidth,
      deviceHeight,
      shouldBringToFront,
      updateShift,
      isIntegrationKiosk,
      automatedBreaksIsEnabled
    } = this.props;

    const shiftOrMultishift = shifts.length > 1 ? 'multiShift' : 'noShift';
    const type: AppScreenType = shift ? 'shift' : shiftOrMultishift;
    const automatedBreaksEnabled =
      this.context.FEATURE_FLAGS.AUTOMATED_BREAKS_FOR_KIOSK &&
      shift &&
      shouldApplyAutomatedBreaksForShift({
        shift,
        automatedBreaksIsEnabled
      });

    if (loadingUserShifts) {
      return (
        <Container shouldBringToFront={shouldBringToFront} deviceHeight={deviceHeight}>
          <Column deviceHeight={deviceHeight}>
            <FormContainer>
              <BrandLoader />
            </FormContainer>
          </Column>
        </Container>
      );
    }

    return (
      <Container
        className={this.getClassNames(hideGreeting, this.state.shouldMoveOut)}
        deviceWidth={deviceWidth}
        deviceHeight={deviceHeight}
        shouldBringToFront={shouldBringToFront}>
        <Column deviceHeight={deviceHeight}>
          <GrowingContainer>
            {!hideGreeting && !this.isGroupPickerStep() && (
              <UserPanelHeader
                user={user}
                type={type}
                shift={shift}
                shifts={shifts}
                customLabel={this.getCustomHeaderLabel(
                  type,
                  adHocShift,
                  usTimeFormat,
                  clockInEarlyMinutes
                )}
                appState={appState}
                appStatusVisible={appStatusVisible}
                shouldHide={this.state.shouldHideHeader}
                shouldDelayAnimation={this.state.shouldDelayHeaderAnimation}
              />
            )}

            {!automatedBreaksEnabled &&
              type === 'shift' &&
              shift &&
              shift.clockInStatus.clockedIn && (
                <ClockedInFields
                  type={APP_STATE_CLOCKED_IN}
                  user={user}
                  usTimeFormat={usTimeFormat}
                  shift={shift}
                  startBreak={startBreak}
                  endBreak={endBreak}
                  clockOut={clockOut}
                  signOut={this.signOut}
                  shiftBreakType={shiftBreakType}
                  deviceHeight={deviceHeight}
                  isIntegrationKiosk={isIntegrationKiosk}
                />
              )}

            {automatedBreaksEnabled && type === 'shift' && shift && shift.clockInStatus.clockedIn && (
              <ClockedInViewProvider
                user={user}
                usTimeFormat={usTimeFormat}
                shift={shift}
                startBreak={startBreak}
                endBreak={endBreak}
                clockOut={clockOut}
                signOut={this.signOut}
                shiftBreakType={shiftBreakType}
                deviceHeight={deviceHeight}
                isIntegrationKiosk={isIntegrationKiosk}
                timeClockNoteRef={this.timeClockNoteRef}
                appState={appState}>
                <AutomatedBreaksProvider startAutomatedBreak={startAutomatedBreak} shift={shift}>
                  <ClockedInViewWithAutomatedBreaks />
                </AutomatedBreaksProvider>
              </ClockedInViewProvider>
            )}

            {type === 'shift' && shift && !shift.clockInStatus.clockedIn && (
              <SingleShiftFields
                shift={shift}
                notes={notes}
                clockIn={clockIn}
                signOut={this.signOut}
                usTimeFormat={usTimeFormat}
                deviceHeight={deviceHeight}
                user={user}
                isIntegrationKiosk={isIntegrationKiosk}
              />
            )}

            {type === 'multiShift' && (
              <MultiShiftFields shifts={shifts} updateShift={updateShift} signOut={this.signOut} />
            )}

            {type === 'noShift' && (
              <NoShiftFields
                personas={personas}
                currentStep={this.state.step}
                clockInNextStep={this.clockInNextStep}
                clockInPrevStep={this.clockInPrevStep}
                notes={notes}
                signOut={this.signOut}
                useLongDelayAnimation={this.state.shouldDelayHeaderAnimation}
                isIntegrationKiosk={isIntegrationKiosk}
                deviceHeight={deviceHeight}
              />
            )}
          </GrowingContainer>
        </Column>
      </Container>
    );
  }
}

UserPanel.propTypes = {
  user: PropTypes.object,
  loadingUserShifts: PropTypes.bool,
  shifts: PropTypes.array,
  shift: PropTypes.object,
  adHocShift: PropTypes.object,
  personas: PropTypes.array,
  notes: PropTypes.array,
  signOut: PropTypes.func,
  clockIn: PropTypes.func,
  startBreak: PropTypes.func,
  startAutomatedBreak: PropTypes.func,
  endBreak: PropTypes.func,
  clockOut: PropTypes.func,
  usTimeFormat: PropTypes.bool,
  clockInEarlyMinutes: PropTypes.number,
  shiftBreakType: PropTypes.bool,
  adHocClockIn: PropTypes.func,
  appState: PropTypes.string,
  appStatusVisible: PropTypes.bool,
  hideGreeting: PropTypes.bool,
  deviceWidth: PropTypes.number,
  deviceHeight: PropTypes.number,
  screen: PropTypes.string,
  shouldBringToFront: PropTypes.bool,
  updateShift: PropTypes.func
};
